import { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { mediaApi } from '../services/api';
import {
  MediaConfigResponse,
  MediaConfigInfo,
  MediaPreview,
  MediaUploadRequest,
  MediaUpdateRequest,
  MediaSearchRequest,
  MediaSearchResponse,
  ApiResponse
} from '../types/api';
import toast from 'react-hot-toast';
import { config } from '../config/environment';

// Storage key para persistência local (v2 para invalidar cache antigo)
const STORAGE_KEY = 'loopra_midias_config_v2';
type ApiErrorResponse = {
  response?: {
    data?: {
      message?: string;
    };
  };
};

const getErrorMessage = (error: unknown, fallback: string) => {
  if (typeof error === 'object' && error !== null) {
    const typed = error as ApiErrorResponse;
    return typed.response?.data?.message ?? fallback;
  }
  return fallback;
};

// Query keys
const scopeKey = (userId?: string) => userId ?? 'anonymous';

export const mediaQueryKeys = {
  all: (userId?: string) => ['medias', scopeKey(userId)] as const,
  config: (userId: string | undefined, filename: string) => ['medias', 'config', scopeKey(userId), filename] as const,
  structure: (userId?: string) => ['medias', 'structure', scopeKey(userId)] as const,
  search: (userId: string | undefined, params: MediaSearchRequest) => ['medias', 'search', scopeKey(userId), params] as const,
};

// Hook principal para gerenciamento de mídias
export const useMidias = (userId?: string) => {
  const queryClient = useQueryClient();
  const [optimisticUploads, setOptimisticUploads] = useState<MediaPreview[]>([]);

  const storageKey = useMemo(() => `${STORAGE_KEY}_${scopeKey(userId)}`, [userId]);

  const readCachedConfig = useCallback((): MediaConfigResponse | null => {
    try {
      const cached = localStorage.getItem(storageKey);
      return cached ? JSON.parse(cached) : null;
    } catch (error) {
      console.warn('[useMidias] Falha ao ler cache local de mídias', error);
      return null;
    }
  }, [storageKey]);

  const cachedInitialData = useMemo((): ApiResponse<MediaConfigResponse> | undefined => {
    const cached = readCachedConfig();
    if (!cached) return undefined;
    return {
      status: 'success',
      message: 'cached',
      data: cached,
    };
  }, [readCachedConfig]);

  const buildPreviewUrl = useCallback(
    (fileName: string, tipo: 'video' | 'image'): string => {
      if (!fileName) return '';
      const baseUrl = config.apiUrl.replace(/\/$/, '');
      const mediaPath = tipo === 'video' ? 'videos' : 'imagens';
      const userQuery = userId ? `?userId=${encodeURIComponent(userId)}` : '';
      const url = `${baseUrl}/api/v1/media/files/${mediaPath}/${encodeURIComponent(fileName)}${userQuery}`;
      console.log('[useMidias] URL construída:', {
        fileName,
        tipo,
        baseUrl,
        userId,
        finalUrl: url
      });
      return url;
    },
    [userId]
  );

  // Query para obter todas as configurações
  const {
    data: mediasData,
    isLoading: isLoadingMidias,
    error: mediasError,
    refetch: refetchMidias
  } = useQuery<ApiResponse<MediaConfigResponse>>({
    queryKey: mediaQueryKeys.all(userId),
    queryFn: async () => {
      const response = await mediaApi.getAll(userId);
      if (response.status === 'success') {
        localStorage.setItem(storageKey, JSON.stringify(response.data));
      }
      return response;
    },
    staleTime: 0, // Forçar reload para pegar URLs atualizadas
    gcTime: 30 * 60 * 1000,
    enabled: Boolean(userId),
  });

  // Query para estrutura/documentação
  const {
    data: structureData,
    isLoading: isLoadingStructure
  } = useQuery({
    queryKey: mediaQueryKeys.structure(userId),
    queryFn: () => mediaApi.getStructure(userId),
    staleTime: 30 * 60 * 1000,
    enabled: Boolean(userId),
  });

  // Mutation para upload de mídia com atualização otimista
  const uploadMutation = useMutation({
    mutationFn: (request: MediaUploadRequest) => mediaApi.upload({ ...request, userId }),
    onMutate: async (request) => {
      await queryClient.cancelQueries({ queryKey: mediaQueryKeys.all(userId) });

      const objectUrl = URL.createObjectURL(request.file);
      const palavrasLista = request.palavras_chave
        .split(',')
        .map((keyword) => keyword.trim())
        .filter(Boolean);

      const optimisticPreview: MediaPreview = {
        fileName: request.file.name,
        tipo: request.file.type.startsWith('video/') ? 'video' : 'image',
        palavrasChave: palavrasLista,
        descricao: request.descricao || '',
        delaySegundos: request.delay_segundos || 3,
        url: objectUrl,
        exists: false,
        ownerId: userId,
      };

      setOptimisticUploads((prev) => {
        const filtered = prev.filter((item) => item.fileName !== optimisticPreview.fileName);
        return [optimisticPreview, ...filtered];
      });

      return { objectUrl, fileName: request.file.name };
    },
    onSuccess: (_, __, context) => {
      toast.success('Mídia carregada com sucesso!');
      if (context?.fileName) {
        setOptimisticUploads((prev) => prev.filter((item) => item.fileName !== context.fileName));
      }
      if (context?.objectUrl) {
        URL.revokeObjectURL(context.objectUrl);
      }
    },
    onError: (error: unknown, _request, context) => {
      const message = getErrorMessage(error, 'Erro ao fazer upload da mídia');
      toast.error(message);
      if (context?.fileName) {
        setOptimisticUploads((prev) => prev.filter((item) => item.fileName !== context.fileName));
      }
      if (context?.objectUrl) {
        URL.revokeObjectURL(context.objectUrl);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: mediaQueryKeys.all(userId) });
      refetchMidias();
    },
  });

  // Mutation para atualizar configuração
  const updateConfigMutation = useMutation({
    mutationFn: ({ filename, config }: { filename: string; config: MediaUpdateRequest }) =>
      mediaApi.updateConfig(filename, { ...config, userId }),
    onSuccess: (_response, variables) => {
      toast.success('Configuração atualizada com sucesso!');
      queryClient.invalidateQueries({ queryKey: mediaQueryKeys.all(userId) });
      queryClient.invalidateQueries({ queryKey: mediaQueryKeys.config(userId, variables.filename) });
    },
    onError: (error: unknown) => {
      const message = getErrorMessage(error, 'Erro ao atualizar configuração');
      toast.error(message);
    },
  });

  // Mutation para remover configuração
  const removeConfigMutation = useMutation({
    mutationFn: (filename: string) => mediaApi.removeConfig(filename, userId),
    onSuccess: (_response, filename) => {
      toast.success('Mídia removida com sucesso!');
      queryClient.invalidateQueries({ queryKey: mediaQueryKeys.all(userId) });
      queryClient.removeQueries({ queryKey: mediaQueryKeys.config(userId, filename) });
    },
    onError: (error: unknown) => {
      const message = getErrorMessage(error, 'Erro ao remover configuração');
      toast.error(message);
    },
  });

  // Mutation para recarregar configurações (silenciosa)
  const reloadMutation = useMutation({
    mutationFn: () => mediaApi.reload(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mediaQueryKeys.all(userId) });
    },
    onError: (error: unknown) => {
      const message = getErrorMessage(error, 'Erro ao recarregar configurações');
      toast.error(message);
    },
  });

  const mediaPreviews = useMemo(() => {
    const serverPreviews: MediaPreview[] = mediasData?.data?.configuracao
      ? Object.entries(mediasData.data.configuracao).map(([fileName, config]) => {
          const mediaConfig = config as MediaConfigInfo;
          const palavrasLista = mediaConfig.palavras_lista?.length
            ? mediaConfig.palavras_lista
            : mediaConfig.palavras_chave
                .split(',')
                .map((keyword) => keyword.trim())
                .filter(Boolean);

          const previewUrl = mediaConfig.preview_url || buildPreviewUrl(fileName, mediaConfig.tipo);

          console.log('[useMidias] Preview gerado:', {
            fileName,
            tipo: mediaConfig.tipo,
            preview_url_backend: mediaConfig.preview_url,
            preview_url_construida: previewUrl,
            owner_id: mediaConfig.owner_id
          });

          return {
            fileName,
            tipo: mediaConfig.tipo,
            palavrasChave: palavrasLista,
            descricao: mediaConfig.descricao || '',
            delaySegundos: mediaConfig.delay_segundos || 3,
            url: previewUrl,
            exists: true,
            ownerId: mediaConfig.owner_id,
          } satisfies MediaPreview;
        })
      : [];

    if (!optimisticUploads.length) {
      return serverPreviews;
    }

    const existingNames = new Set(serverPreviews.map((preview) => preview.fileName));
    const pendingPreviews = optimisticUploads.filter((preview) => !existingNames.has(preview.fileName));

    return [...pendingPreviews, ...serverPreviews];
  }, [mediasData, buildPreviewUrl, optimisticUploads]);

  const availableFiles = useMemo(() => {
    return mediasData?.data?.arquivos_disponiveis || { imagens: [], videos: [] };
  }, [mediasData]);

  const statistics = useMemo(() => {
    return mediasData?.data?.estatisticas || {
      total_midias: 0,
      usuarios_ativos: 0,
      videos_count: 0,
      imagens_count: 0,
    };
  }, [mediasData]);

  const documentation = useMemo(() => {
    return mediasData?.data?.documentacao || {
      formato_configuracao: '',
      exemplos: [],
      instrucoes: [],
      intencoes_sugeridas: {},
    };
  }, [mediasData]);

  return {
    // Dados
    mediasData: mediasData?.data,
    mediaPreviews,
    availableFiles,
    statistics,
    documentation,
    structure: structureData?.data,
    cachedData: readCachedConfig(),

    // Estados de loading
    isLoadingMidias,
    isLoadingStructure,
    isUploading: uploadMutation.isPending,
    isUpdating: updateConfigMutation.isPending,
    isRemoving: removeConfigMutation.isPending,
    isReloading: reloadMutation.isPending,

    // Erros
    mediasError,

    // Ações
    uploadMedia: uploadMutation.mutate,
    updateMediaConfig: updateConfigMutation.mutate,
    removeMediaConfig: removeConfigMutation.mutate,
    reloadConfigurations: reloadMutation.mutate,
    refetchMidias,

    // Utilitários
    invalidateCache: () => {
      queryClient.invalidateQueries({ queryKey: mediaQueryKeys.all(userId) });
      localStorage.removeItem(storageKey);
    },
  };
};

// Hook para busca de mídias
export const useMediaSearch = (request: MediaSearchRequest, userId?: string, enabled = false) => {
  return useQuery({
    queryKey: mediaQueryKeys.search(userId, request),
    queryFn: () => mediaApi.search(request, userId),
    enabled: enabled && Boolean(userId) && !!(request.mensagem || request.contexto),
    staleTime: 2 * 60 * 1000,
  });
};

// Hook para configuração específica de mídia
export const useMediaConfig = (filename: string, userId?: string, enabled = true) => {
  return useQuery({
    queryKey: mediaQueryKeys.config(userId, filename),
    queryFn: () => mediaApi.getConfig(filename, userId),
    enabled: enabled && Boolean(userId) && !!filename,
    staleTime: 5 * 60 * 1000,
  });
};

// Hook para validação de arquivo
export const useFileValidation = () => {
  const validateFile = useCallback((file: File) => {
    const maxSize = 20 * 1024 * 1024; // 20MB - limite para WhatsApp
    const allowedTypes = [
      'image/jpeg',
      'image/png',
      'image/gif',
      'video/mp4',
      'video/mov',
      'video/avi',
    ];

    if (file.size > maxSize) {
      return { valid: false, error: 'Arquivo muito grande. Máximo 20MB.' };
    }

    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: 'Tipo de arquivo não suportado. Use: PNG, JPG, GIF, MP4, MOV, AVI.'
      };
    }

    return { valid: true, error: null };
  }, []);

  const getFileType = useCallback((file: File): 'video' | 'image' => {
    return file.type.startsWith('video/') ? 'video' : 'image';
  }, []);

  const compressImage = useCallback(async (file: File, maxSizeMB = 15): Promise<File> => {
    return new Promise((resolve, reject) => {
      if (!file.type.startsWith('image/')) {
        resolve(file);
        return;
      }

      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Redimensionar apenas se for muito grande (WhatsApp suporta até 4096x4096)
          const maxWidth = 4096;
          const maxHeight = 4096;

          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width = width * ratio;
            height = height * ratio;
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);

          // Comprimir com qualidade ajustável baseada no tamanho original
          const fileSizeMB = file.size / 1024 / 1024;
          let quality = fileSizeMB > 10 ? 0.7 : 0.85; // Qualidade inicial baseada no tamanho

          const tryCompress = (qual: number) => {
            canvas.toBlob(
              (blob) => {
                if (!blob) {
                  reject(new Error('Erro ao comprimir imagem'));
                  return;
                }

                const sizeMB = blob.size / 1024 / 1024;

                // Se ainda está grande e podemos reduzir mais a qualidade
                if (sizeMB > maxSizeMB && qual > 0.4) {
                  tryCompress(qual - 0.05);
                  return;
                }

                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });
                resolve(compressedFile);
              },
              'image/jpeg',
              qual
            );
          };

          tryCompress(quality);
        };
        img.onerror = () => reject(new Error('Erro ao carregar imagem'));
      };
      reader.onerror = () => reject(new Error('Erro ao ler arquivo'));
    });
  }, []);

  return {
    validateFile,
    getFileType,
    compressImage,
  };
};

// Hook para sugestões de palavras-chave
export const useKeywordSuggestions = (userId?: string) => {
  const { structure } = useMidias(userId);

  const getSuggestions = useCallback((input: string) => {
    const intentions = structure?.intencoes_palavras_chave ?? {};
    if (!input) return [];

    const normalizedInput = input.toLowerCase();
    const suggestions = new Set<string>();

    const keywordGroups = Object.values(intentions) as string[][];
    keywordGroups.forEach((keywords) => {
      keywords.forEach((keyword) => {
        if (keyword.toLowerCase().includes(normalizedInput)) {
          suggestions.add(keyword);
        }
      });
    });

    return Array.from(suggestions).slice(0, 10);
  }, [structure]);

  const getIntentions = useCallback(() => {
    return structure?.intencoes_palavras_chave || {};
  }, [structure]);

  return {
    getSuggestions,
    getIntentions,
    intentions: getIntentions(),
  };
};